import {
	Button,
	IconButton,
	Stack,
	Tooltip,
	tooltipClasses,
	Typography,
	useTheme,
} from '@mui/material';
import {translations} from 'presentation/translations-new';
import {textHandle} from 'presentation/utils/textHandle';
import React from 'react';
import {Loading} from 'presentation/components/Loading/Loading';
import Icon from 'presentation/icons';
import {ExamResultsTaggingEnum} from 'main/analytics/gaTagsEnum';
import {
	StyledButtonContainer,
	StyledDot,
	StyledItemContainer,
	StyledStatusContainer,
	StyledStatusTypography,
} from './styles';
import {ExamReportItemProps} from './types';
import {useMedicalReportItem} from 'presentation/modules/examResults/hooks/useMedicalReportItem';

const {buttonShowImage, inAnalysis, releasedIn} =
	translations['pt-br'].privateFlow.myHospitalization.exams.drawer;
const {download, share} = translations['pt-br'].commons;

export const ExamReportItem = ({exam, isFirst}: ExamReportItemProps) => {
	const theme = useTheme();

	const {
		openImageUrl,
		urlPacsmobile,
		openPdf,
		showExamReportItemActions,
		disableButtons,
		downloadButtonIsLoading,
		shareButtonIsLoading,
	} = useMedicalReportItem({
		medicalReport: exam,
	});

	return (
		<StyledItemContainer $first={isFirst}>
			<Stack>
				<Typography color="text.secondary">
					{textHandle(exam.dsProcedimento.toLowerCase()).capitalizeFirstLetter()}
				</Typography>
				<StyledStatusContainer>
					<StyledDot $isReleased={!!exam?.dtLaudo} />
					<StyledStatusTypography>
						{exam.dtLaudo ? `${releasedIn}${exam.dtLaudo.split(' ')[0]}` : inAnalysis}
					</StyledStatusTypography>
				</StyledStatusContainer>
			</Stack>
			<StyledButtonContainer>
				{showExamReportItemActions ? (
					<Stack direction="row">
						<Tooltip
							title={download}
							enterTouchDelay={0}
							slotProps={{
								popper: {
									sx: {
										[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
											{marginTop: 0.5, fontWeight: 600},
									},
								},
							}}>
							<IconButton
								disabled={disableButtons}
								onClick={() => openPdf()}
								aria-label="Baixar laudo do exame"
								data-tag-id={
									ExamResultsTaggingEnum.CLICK_BAIXAR_LAUDO_EXAME_LIBERADOS
								}>
								{downloadButtonIsLoading ? (
									<Loading size={24} color="inherit" />
								) : (
									<Icon
										name="download-file"
										color={
											disableButtons
												? theme.palette.text.disabled
												: theme.palette.primary.main
										}
									/>
								)}
							</IconButton>
						</Tooltip>
						<Tooltip
							title={share}
							enterTouchDelay={0}
							slotProps={{
								popper: {
									sx: {
										[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
											{marginTop: '4px', fontWeight: 600},
									},
								},
							}}>
							<IconButton
								disabled={disableButtons}
								onClick={() => openPdf(true)}
								aria-label="Compartilhar resultado do exame"
								data-tag-id={
									ExamResultsTaggingEnum.CLICK_COMPARTILHAR_RESULTADO_DE_EXAMES_LIBERADOS
								}>
								{shareButtonIsLoading ? (
									<Loading size={24} color="inherit" />
								) : (
									<Icon
										name="share-filled"
										color={
											disableButtons
												? theme.palette.text.disabled
												: theme.palette.primary.main
										}
									/>
								)}
							</IconButton>
						</Tooltip>
					</Stack>
				) : null}

				{urlPacsmobile ? (
					<Button
						variant="outlined"
						size="small"
						onClick={openImageUrl}
						data-tag-id={ExamResultsTaggingEnum.CLICK_VER_IMAGEM_EXAME}>
						{buttonShowImage}
					</Button>
				) : null}
			</StyledButtonContainer>
		</StyledItemContainer>
	);
};
